import type { HealthNode } from "@tanstack/db";
import type {
  WikiPageListPublic,
  WikiPagePublic,
  WikiPermissionsPublic,
  WikiRevisionPublic,
} from "@softmax/observatory-client/types";
import type { WikiPage, WikiViewProps } from "@softmax/web-ux/wiki";
import {
  collectionError,
  collectionRefreshing,
} from "@softmax/web-features/data";
import { curatedText } from "@softmax/web-features/coworlds";
import { loadState, revisionView } from "./wiki-presentation";

type CollectionRead<T> = {
  readonly data: T | undefined;
  readonly health: HealthNode | undefined;
  readonly isReady: boolean;
};

export function wikiReadStates({
  permissions,
  pageQuery,
  indexQuery,
  revisionsQuery,
  missing,
  accessLost,
  accessError,
  indexAccessLost,
  refreshing,
}: {
  readonly permissions: CollectionRead<
    WikiPermissionsPublic | null | undefined
  >;
  readonly pageQuery: CollectionRead<WikiPagePublic | null | undefined>;
  readonly indexQuery: CollectionRead<
    readonly WikiPageListPublic["entries"][number][]
  >;
  readonly revisionsQuery: CollectionRead<readonly WikiRevisionPublic[]>;
  readonly missing: boolean;
  readonly accessLost: boolean;
  readonly accessError: Error | null;
  readonly indexAccessLost: boolean;
  readonly refreshing: boolean;
}): Pick<WikiViewProps, "page" | "index" | "history" | "permissions"> {
  const page = pageQuery.data;
  let pageView: WikiPage | null | undefined;

  if (page && !accessLost) {
    pageView = {
      id: page.id,
      title: curatedText(page.title),
      currentRevisionId: page.current_revision_id,
      currentRevision: revisionView(page.current_revision),
    };
  } else if (missing && !accessLost) {
    pageView = null;
  }

  return {
    page: loadState({
      data: pageView,
      fetching: refreshing || collectionRefreshing(pageQuery.health),
      error:
        accessError ??
        collectionError(permissions.health) ??
        collectionError(pageQuery.health),
    }),
    index: loadState({
      data:
        accessLost || indexAccessLost || !indexQuery.isReady
          ? undefined
          : indexQuery.data?.map(({ id, slug, title }) => ({
              id,
              slug,
              title: curatedText(title),
            })),
      fetching: refreshing || collectionRefreshing(indexQuery.health),
      error: collectionError(indexQuery.health),
    }),
    history: loadState({
      data:
        accessLost || !revisionsQuery.isReady
          ? undefined
          : revisionsQuery.data?.map(revisionView),
      fetching: refreshing || collectionRefreshing(revisionsQuery.health),
      error: collectionError(revisionsQuery.health),
    }),
    permissions: {
      canEdit: !accessLost && permissions.data?.can_edit === true,
      canDelete: !accessLost && permissions.data?.can_delete === true,
    },
  };
}
